import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useApi } from "@/api/hooks/useApi";
import type { TicketStatusRequest } from "@/api/types";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { useProjectId } from "@/hooks/Network/useProjectId";
import { QUERY_KEYS } from "@/query-keys";
import { useTranslation } from "@/translations";

const useCreateTicketStatus = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: TicketStatusRequest) => api.postTicketStatusesV1(data),
    onSuccess() {
      showFlashToast({
        variant: "success",
        message: t("page.ticket-status.action.create.notification.success"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKET_STATUSES(projectId),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.ticket-status.action.create.notification.error"),
      });
    },
  });
};

const useUpdateTicketStatus = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: TicketStatusRequest }) => api.putTicketStatusesV1(id, data),
    onSuccess(_, { id }) {
      showFlashToast({
        variant: "success",
        message: t("page.ticket-status.action.edit.notification.success"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKET_STATUS(projectId, id),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKET_STATUSES(projectId),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.ticket-status.action.edit.notification.error"),
      });
    },
  });
};

const useDeleteTicketStatus = () => {
  const api = useApi();
  const projectId = useProjectId();
  const queryClient = useQueryClient();
  const showFlashToast = useFlashToast();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, replacementStatusId }: { id: string; replacementStatusId?: string }) =>
      api.deleteTicketStatusesByIdV1(id, { replacementStatusId }),
    onSuccess() {
      showFlashToast({
        variant: "success",
        message: t("page.ticket-status.list.action.delete.notification.success"),
      });
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TICKET_STATUSES(projectId),
      });
    },
    onError() {
      showFlashToast({
        variant: "alert",
        message: t("page.ticket-status.list.action.delete.notification.error"),
      });
    },
  });
};

export const ticketStatusMutations = {
  useCreateTicketStatus,
  useUpdateTicketStatus,
  useDeleteTicketStatus,
};
